import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Sparkles, Timer, CheckCircle2, Download } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { api, API_BASE_URL } from "../lib/api";
import { WHATSAPP_COMMUNITY_URL } from "../lib/constants";

const TIME_LIMIT = 900; // 15 minutes for 20 questions

export default function TcfDiagnostic() {
  const { t, lang } = useLang();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [index, setIndex] = useState(0);
  const [started, setStarted] = useState(false);
  const [seconds, setSeconds] = useState(TIME_LIMIT);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [diagnosticId, setDiagnosticId] = useState(null);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [subscribe, setSubscribe] = useState(true);

  useEffect(() => {
    api
      .get(`/tcf/diagnostic/questions?lang=${lang}`)
      .then((r) => setQuestions(Array.isArray(r.data?.questions) ? r.data.questions : []))
      .catch(() => setQuestions([]));
  }, [lang]);

  useEffect(() => {
    if (!started || result) return;
    const id = setInterval(() => {
      setSeconds((s) => {
        if (s <= 1) {
          clearInterval(id);
          submit();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [started, result]);

  const pick = (i) => setAnswers((a) => ({ ...a, [index]: i }));
  const next = () => setIndex((i) => Math.min(Math.max(0, questions.length - 1), i + 1));
  const prev = () => setIndex((i) => Math.max(0, i - 1));

  const submit = async () => {
    if (submitting || result) return;
    setSubmitting(true);
    try {
      const arr = (questions || []).map((_, i) => (answers[i] === undefined ? -1 : answers[i]));
      const payload = { answers: arr, language: lang };
      if (email) payload.email = email;
      if (firstName) payload.first_name = firstName;
      payload.subscribe_newsletter = !!(subscribe && email);
      const r = await api.post("/tcf/diagnostic", payload);
      if (r.data) {
        setResult(r.data.result || null);
        setDiagnosticId(r.data.diagnostic_id || null);
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      alert(t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  const answered = Object.keys(answers).length;
  const q = questions[index];
  const min = Math.floor(seconds / 60);
  const sec = String(seconds % 60).padStart(2, "0");

  // START SCREEN
  if (!started && !result) {
    return (
      <div className="bg-alabaster min-h-[80vh]" data-testid="tcf-diagnostic-start">
        <section className="max-w-[900px] mx-auto px-6 lg:px-10 pt-24 pb-16 text-center">
          <p className="text-[10px] tracking-[0.3em] uppercase text-terracotta mb-4">
            {lang === "fr" ? "— Diagnostic TCF Canada" : "— TCF Canada diagnostic"}
          </p>
          <h1 className="font-serif text-5xl lg:text-7xl text-navy tracking-tight leading-[0.95]">
            {lang === "fr" ? "Où en êtes-vous en français ?" : "Where are you in French?"}
          </h1>
          <p className="mt-8 text-navy/70 text-lg max-w-lg mx-auto leading-relaxed">
            {lang === "fr"
              ? "20 questions en français au format TCF Canada (compréhension écrite). Environ 15 minutes. Un niveau estimé (A1 → C1) et une recommandation de préparation TCF personnalisée."
              : "20 French questions in the TCF Canada reading format. About 15 minutes. Get an estimated level (A1 → C1) and a personalized TCF prep recommendation."}
          </p>
          <div className="mt-10 grid grid-cols-3 gap-4 max-w-md mx-auto text-navy/60 text-xs tracking-[0.25em] uppercase">
            <div><Timer size={16} className="mx-auto mb-2 text-terracotta" />~15 min</div>
            <div><Sparkles size={16} className="mx-auto mb-2 text-terracotta" />{lang === "fr" ? "IRCC-aligné" : "IRCC-aligned"}</div>
            <div><CheckCircle2 size={16} className="mx-auto mb-2 text-terracotta" />{lang === "fr" ? "Gratuit" : "Free"}</div>
          </div>
          <button
            onClick={() => setStarted(true)}
            data-testid="tcf-diag-start"
            className="mt-12 inline-flex items-center justify-center gap-3 px-10 py-4 rounded-full bg-[#06162A] text-white text-base font-bold shadow-xl hover:bg-[#D71920] active:scale-95 transition-all cursor-pointer group"
          >
            <span>{lang === "fr" ? "Commencer le test gratuit" : "Start the free test"}</span>
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </button>
          <p className="mt-6 text-xs text-navy/50">
            {lang === "fr" ? "Vos réponses restent privées." : "Your answers stay private."}
          </p>
        </section>
      </div>
    );
  }

  // RESULT SCREEN
  if (result) {
    const bandColors = { A1: "text-navy/60", A2: "text-navy/70", B1: "text-navy", B2: "text-terracotta", C1: "text-gold" };
    const rec = result.recommendation;
    return (
      <div className="bg-alabaster min-h-[80vh]" data-testid="tcf-diagnostic-result">
        <section className="max-w-[1100px] mx-auto px-6 lg:px-10 pt-24 pb-24">
          <div className="text-center mb-10">
            <p className="text-[10px] tracking-[0.3em] uppercase text-terracotta mb-4">
              {lang === "fr" ? "— Votre résultat" : "— Your result"}
            </p>
            <motion.p
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className={`font-serif text-[10rem] leading-none tracking-tight ${bandColors[result.estimated_band]}`}
              data-testid="diag-band"
            >
              {result.estimated_band}
            </motion.p>
            <p className="mt-4 font-serif text-3xl text-navy" data-testid="diag-clb">CLB {result.estimated_clb}</p>
            <p className="text-navy/60 text-sm mt-2">{result.percentage}% {lang === "fr" ? "de réussite pondérée" : "weighted score"}</p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-navy text-alabaster rounded-2xl p-8 lg:p-12 relative overflow-hidden"
          >
            <p className="text-[10px] tracking-[0.3em] uppercase text-gold mb-4">
              {lang === "fr" ? "Notre recommandation" : "Our recommendation"}
            </p>
            <h2 className="font-serif text-4xl lg:text-5xl leading-tight tracking-tight">
              {rec === "tcf_complete"
                ? (lang === "fr" ? "TCF Canada — Préparation Complète" : "TCF Canada — Complete Prep")
                : (lang === "fr" ? "TCF Canada — Pack Débutant" : "TCF Canada — Starter Pack")}
            </h2>
            <p className="mt-4 text-alabaster/75 max-w-lg leading-relaxed">
              {lang === "fr" ? result.recommendation_reason_fr : result.recommendation_reason_en}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/tcf"
                data-testid="diag-cta-tcf"
                className="inline-flex items-center gap-2 bg-[#D71920] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-gold hover:text-navy transition shadow-md"
              >
                <span>{lang === "fr" ? "Voir les packs" : "See the packs"}</span>
                <ArrowRight size={14} />
              </Link>
              <Link
                to="/booking"
                className="inline-flex items-center gap-2 border border-white/40 text-white px-6 py-3 rounded-full text-sm font-medium hover:border-gold hover:text-gold transition"
              >
                <span>{lang === "fr" ? "Parler à Josias" : "Talk to Josias"}</span>
              </Link>
            </div>
          </motion.div>

          {/* Roadmap PDF download */}
          {diagnosticId && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mt-6 bg-white border border-[#D71920]/20 shadow-sm rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-5"
              data-testid="diag-roadmap-card"
            >
              <span className="w-12 h-12 rounded-full bg-[#D71920] text-white grid place-items-center shrink-0">
                <Download size={18} />
              </span>
              <div className="flex-1">
                <p className="text-[10px] tracking-[0.25em] uppercase text-[#D71920] font-semibold mb-1">
                  {lang === "fr" ? "Feuille de route personnalisée" : "Personalized roadmap"}
                </p>
                <p className="font-serif text-lg text-navy leading-tight">
                  {lang === "fr"
                    ? `Votre plan TCF en 4 étapes pour passer de ${result.estimated_band} au CLB ciblé.`
                    : `Your 4-step TCF plan to move from ${result.estimated_band} to your target CLB.`}
                </p>
                <p className="text-navy/60 text-xs mt-1">
                  {lang === "fr" ? "PDF · 2 pages · gratuit" : "PDF · 2 pages · free"}
                </p>
              </div>
              <a
                href={`${API_BASE_URL}/tcf/diagnostic/${diagnosticId}/roadmap.pdf?lang=${lang}`}
                target="_blank"
                rel="noreferrer"
                data-testid="diag-download-pdf"
                className="inline-flex items-center gap-2 bg-[#06162A] text-white px-5 py-3 rounded-full text-sm font-semibold hover:bg-[#D71920] transition whitespace-nowrap shadow-sm"
              >
                <Download size={14} />
                <span>{lang === "fr" ? "Télécharger" : "Download"}</span>
              </a>
            </motion.div>
          )}

          {/* WhatsApp community */}
          <motion.a
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            href={WHATSAPP_COMMUNITY_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="diag-community-cta"
            className="mt-6 flex items-center gap-4 bg-cream border border-navy/10 rounded-2xl p-5 hover:border-terracotta hover:shadow-lg transition group"
          >
            <span className="w-11 h-11 rounded-full bg-[#25D366] text-white grid place-items-center shrink-0 group-hover:scale-110 transition">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.693.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            </span>
            <div className="flex-1">
              <p className="text-[10px] tracking-[0.25em] uppercase text-terracotta mb-1">
                {lang === "fr" ? "Communauté" : "Community"}
              </p>
              <p className="font-serif text-lg text-navy leading-tight">
                {lang === "fr"
                  ? "Rejoignez le groupe WhatsApp — questions, astuces, entraide francophone."
                  : "Join the WhatsApp group — questions, tips, francophone support."}
              </p>
            </div>
            <ArrowRight size={18} className="text-navy/40 group-hover:text-terracotta group-hover:translate-x-1 transition" />
          </motion.a>

          {/* Optional capture */}
          {!email && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="mt-8 bg-white border border-navy/10 rounded-2xl p-8"
            >
              <p className="font-serif text-2xl text-navy mb-3">
                {lang === "fr" ? "Recevez votre feuille de route par courriel" : "Get your prep roadmap by email"}
              </p>
              <p className="text-navy/60 text-sm mb-5">
                {lang === "fr" ? "PDF personnalisé + 1 conseil TCF gagnant par mois de Josias." : "Personalized PDF + 1 winning TCF tip per month from Josias."}
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder={lang === "fr" ? "Prénom" : "First name"} className="h-11 px-3 rounded-lg border border-navy/15 focus:border-terracotta focus:outline-none text-sm" data-testid="diag-first-name" />
                <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="email@…" className="flex-1 h-11 px-3 rounded-lg border border-navy/15 focus:border-terracotta focus:outline-none text-sm" data-testid="diag-email" />
                <button
                  onClick={async () => {
                    if (!email) return;
                    try {
                      const r = await api.post("/tcf/diagnostic", {
                        answers: (questions || []).map((_, i) => (answers[i] === undefined ? -1 : answers[i])),
                        email, first_name: firstName || null, language: lang, subscribe_newsletter: subscribe,
                      });
                      if (r?.data?.diagnostic_id) setDiagnosticId(r.data.diagnostic_id);
                    } catch (err) {
                      console.warn("[TcfDiagnostic] email capture submit failed", err);
                    }
                    setSubscribe(false);
                  }}
                  data-testid="diag-send-email"
                  className="h-11 px-6 rounded-full bg-[#06162A] text-white font-semibold hover:bg-[#D71920] text-sm transition cursor-pointer shadow-md"
                >
                  <span>{lang === "fr" ? "Envoyer" : "Send"}</span>
                </button>
              </div>
              <label className="mt-4 flex items-start gap-2 text-xs text-navy/60 cursor-pointer">
                <input type="checkbox" checked={subscribe} onChange={(e) => setSubscribe(e.target.checked)} className="mt-0.5" />
                <span>{lang === "fr" ? "Inscription à l'infolettre (1 conseil par mois, désabonnement facile)." : "Subscribe to the newsletter (1 tip per month, easy unsubscribe)."}</span>
              </label>
            </motion.div>
          )}
        </section>
      </div>
    );
  }

  // QUIZ SCREEN
  if (!q) return <div className="max-w-[900px] mx-auto px-6 py-32 text-navy/60">{t("common.loading")}</div>;

  const allAnswered = answered === (questions?.length || 0);

  return (
    <div className="bg-alabaster min-h-[80vh]" data-testid="tcf-diagnostic-quiz">
      <section className="max-w-[900px] mx-auto px-6 lg:px-10 pt-24 pb-16">
        {/* Progress + timer */}
        <div className="flex items-center justify-between mb-8">
          <p className="text-xs tracking-[0.25em] uppercase text-navy/60">
            {index + 1} / {questions?.length || 0}
          </p>
          <p className={`font-mono text-sm ${seconds < 30 ? "text-terracotta" : "text-navy/60"}`} data-testid="diag-timer">
            <Timer size={12} className="inline mr-1" /> {min}:{sec}
          </p>
        </div>
        <div className="h-1 bg-navy/10 rounded-full overflow-hidden mb-10">
          <div className="h-full bg-terracotta transition-all duration-500" style={{ width: `${((index + 1) / Math.max(1, questions?.length || 1)) * 100}%` }} />
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={index}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.25 }}
          >
            <p className="text-[10px] tracking-[0.3em] uppercase text-terracotta mb-4">{q.band}</p>
            {q.context && (
              <div className="mb-6 p-5 rounded-xl bg-cream border border-navy/8 text-navy/85 leading-relaxed" data-testid="diag-context">
                <p className="text-[10px] tracking-[0.25em] uppercase text-navy/40 mb-2">
                  {lang === "fr" ? "Document" : "Passage"}
                </p>
                <p className="font-serif text-lg italic">« {q.context} »</p>
              </div>
            )}
            <h2 className="font-serif text-2xl lg:text-3xl text-navy leading-tight mb-8" data-testid="diag-question">
              {q.question}
            </h2>

            <div className="space-y-2">
              {(q.options || []).map((opt, i) => {
                const selected = answers[index] === i;
                return (
                  <button
                    key={`${q.id}-${i}`}
                    onClick={() => pick(i)}
                    data-testid={`diag-option-${index}-${i}`}
                    className={`w-full text-left px-5 py-4 rounded-xl border transition cursor-pointer ${
                      selected
                        ? "bg-[#06162A] text-white border-[#06162A] shadow-md"
                        : "bg-white text-navy border-navy/10 hover:border-[#D71920]"
                    }`}
                  >
                    <span className={`inline-block w-5 h-5 rounded-full border mr-3 align-middle text-[10px] leading-none pt-1 text-center font-bold ${selected ? "border-white/60 bg-white/20 text-white" : "border-navy/30 text-navy"}`}>
                      {String.fromCharCode(65 + i)}
                    </span>
                    <span className={selected ? "text-white font-medium" : "text-navy"}>{opt}</span>
                  </button>
                );
              })}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Nav */}
        <div className="mt-10 flex items-center justify-between">
          <button
            onClick={prev}
            disabled={index === 0}
            data-testid="diag-prev"
            className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ArrowLeft size={14} /> {lang === "fr" ? "Précédent" : "Previous"}
          </button>
          {index < questions.length - 1 ? (
            <button
              onClick={next}
              disabled={answers[index] === undefined}
              data-testid="diag-next"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#06162A] text-white text-sm font-semibold hover:bg-[#D71920] active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer shadow-md"
            >
              <span>{lang === "fr" ? "Suivant" : "Next"}</span>
              <ArrowRight size={15} />
            </button>
          ) : (
            <button
              onClick={submit}
              disabled={!allAnswered || submitting}
              data-testid="diag-submit"
              className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#D71920] text-white text-sm font-bold hover:bg-[#B91219] active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer shadow-lg"
            >
              <span>{submitting ? t("common.loading") : (lang === "fr" ? "Voir mon niveau" : "See my level")}</span>
              <Sparkles size={16} />
            </button>
          )}
        </div>
      </section>
    </div>
  );
}
